import axios from "axios";
import { getCsrfToken, setCsrfToken, setupCsrfInterceptor } from "./csrfUtils";

axios.defaults.withCredentials = true;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

// Setup CSRF protection for the default axios instance
setupCsrfInterceptor(axios);

const baseUrl = "/api/auth";

// Get current user session data on app init/reload
export const initializeUserSession = async () => {
  try {
    const response = await axios.get(`${baseUrl}/current_user`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      withCredentials: true,
    });

    if (response.status === 200) {
      // Store CSRF token from response
      if (response.data.csrf_token) {
        setCsrfToken(response.data.csrf_token);
      }

      if (response.data.user) {
        return response.data.user; // Return the user data
      } else {
        throw new Error("No user session found");
      }
    }
    return false;
  } catch (error) {
    // Handle 401 as normal "no session" case, not an error
    if (error.response && error.response.status === 401) {
      throw new Error("No user session found");
    }

    // Handle other HTTP errors (403, 404, 500, etc.) as actual errors
    if (error.response) {
      throw new Error(`Network error - please check your connection`);
    }

    // Handle network errors
    if (error.request) {
      throw new Error("Network error - please check your connection");
    }

    // Handle other errors
    throw error;
  }
};

// API call for login authorization
export const login = async (username, password) => {
  try {
    const response = await axios.post(
      `${baseUrl}/`,
      { username, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data.csrf_token) {
      setCsrfToken(response.data.csrf_token);
    }

    return response.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    } else {
      console.error("Login API Error:", error);
      throw new Error("A network error occurred. Please try again.");
    }
  }
};

// DEPRECATED: This function will be removed once Login.js is refactored.
export const handleLogin = async (
  username,
  password,
  setUser,
  navigate,
  setError
) => {
  try {
    const response = await axios.post(
      `${baseUrl}/`,
      { username, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    // Here we will handle the first login issues
    if (response.data.first_login) {
      navigate("/password_change");
      alert(response.data.message);
    } else {
      // Store CSRF token from response
      if (response.data.csrf_token) {
        setCsrfToken(response.data.csrf_token);
      }

      setUser(response.data.user);
      navigate("/home");
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.error) {
      setError(error.response.data.error || "An error occurred during login");
    } else {
      console.log(error);
      setError("Network error. Please try again.");
    }
  }
};

// Handle logout - simplified version
export const handleLogout = async () => {
  try {
    const response = await axios.get(`${baseUrl}/logout`);
    if (response.status === 200) {
      // Clear CSRF token on logout
      setCsrfToken(null);
      return true;
    } else {
      console.error("Logout failed:", response.status);
      return false;
    }
  } catch (error) {
    console.error("Logout failed:", error);
    // Even if logout API fails, we should still clear the client state
    setCsrfToken(null);
    return false;
  }
};

// Check session validity
export const checkSessionValidity = async () => {
  try {
    const response = await axios.get(`${baseUrl}/session`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      withCredentials: true,
    });

    // Store refreshed CSRF token if it's included in the response
    if (response.data && response.data.csrf_token) {
      setCsrfToken(response.data.csrf_token);
    }

    return response.status === 200;
  } catch (error) {
    console.error("Session check failed:", error);
    return false;
  }
};

//sending password reset request information
export const sendPasswordResetRequest = async (username) => {
  const response = await axios.post(`${baseUrl}/password_change_request`, {
    headers: {
      "Content-Type": "application/json",
    },
    username: username,
  });
  return response;
};

//sending password change request information to actually change the password
export const sendPasswordChangeRequest = async (
  username,
  password,
  password_reset_code
) => {
  const response = await axios.post(`${baseUrl}/password_change`, {
    headers: {
      "Content-Type": "application/json",
    },
    username: username,
    password: password,
    password_reset_code: password_reset_code,
  });
  return response;
};
